if (!customElements.get('mc-hero-sequence')) {
  /*
   * Plays a frame sequence on a canvas over the first frame, which stays as the fallback image.
   * Frames download only after the page has loaded; reduced motion keeps the first frame.
   *
   * Optional attributes:
   *   data-load="visible"         download frames when the element approaches the viewport.
   *   data-progress-root="<sel>"  scroll progress spans this ancestor (sticky layouts).
   * Scroll playback dispatches a bubbling 'mc-sequence:progress' event with detail.progress (0–1).
   */
  class MontecapraHeroSequence extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.canvas = this.querySelector('canvas');
      this.context = this.canvas ? this.canvas.getContext('2d') : null;
      if (!this.context) return;

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const source = (mobile ? this.dataset.framesMobile : this.dataset.frames) || '';
      this.urls = source.split('|').filter(Boolean);
      if (this.urls.length < 2) return;

      this.fps = Math.min(30, Math.max(6, Number(this.dataset.fps) || 24));
      this.playback = this.dataset.playback || 'once';
      this.progressRoot = this.dataset.progressRoot ? this.closest(this.dataset.progressRoot) : null;
      this.current = -1;

      this.onResize = () => this.resize();
      this.onScroll = () => this.requestScrollFrame();
      this.start = () => {
        const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
        idle(() => this.load(), { timeout: 2000 });
      };
      this.schedule = () => {
        if (document.readyState === 'complete') {
          this.start();
        } else {
          window.addEventListener('load', this.start, { once: true });
        }
      };

      if (this.dataset.load === 'visible' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.schedule();
          },
          { rootMargin: '100% 0px' }
        );
        this.observer.observe(this.progressRoot || this);
      } else {
        this.schedule();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('load', this.start);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.frameRequest);
      this.disconnected = true;
    }

    load() {
      this.images = this.urls.map((url) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = url;
        return image;
      });

      Promise.all(this.images.map((image) => image.decode().catch(() => null))).then(() => {
        if (this.disconnected) return;
        this.images = this.images.filter((image) => image.naturalWidth > 0);
        if (this.images.length < 2) return;

        this.resize();
        window.addEventListener('resize', this.onResize, { passive: true });
        this.draw(0);
        this.classList.add('is-ready');

        if (this.playback === 'scroll') {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.requestScrollFrame();
        } else {
          this.playOnce();
        }
      });
    }

    resize() {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(this.clientWidth * ratio);
      this.canvas.height = Math.round(this.clientHeight * ratio);
      if (this.current > -1) {
        const index = this.current;
        this.current = -1;
        this.draw(index);
      }
    }

    draw(index) {
      if (index === this.current) return;
      const image = this.images[index];
      if (!image) return;

      const { width, height } = this.canvas;
      const styles = getComputedStyle(this);
      const contain = styles.getPropertyValue('--hp-fit').trim() === 'contain';
      const fit = contain ? Math.min : Math.max;
      const scale = fit(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      let focalProperty = '--hero-focal-mobile';
      if (window.matchMedia('(min-width: 990px)').matches) focalProperty = '--hero-focal-desktop';
      else if (window.matchMedia('(min-width: 750px)').matches) focalProperty = '--hero-focal';
      const focal = contain ? '50% 50%' : styles.getPropertyValue(focalProperty).trim() || '50% 50%';
      const [x = '50%', y = '50%'] = focal.split(/\s+/);
      const offsetX = (width - drawWidth) * (parseFloat(x) / 100);
      const offsetY = (height - drawHeight) * (parseFloat(y) / 100);

      if (contain) this.context.clearRect(0, 0, width, height);
      this.context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
      this.current = index;
    }

    playOnce() {
      const interval = 1000 / this.fps;
      let last = performance.now();
      let index = 0;

      const step = (now) => {
        if (this.disconnected) return;
        if (now - last >= interval) {
          last = now;
          index += 1;
          this.draw(index);
        }
        if (index < this.images.length - 1) this.frameRequest = requestAnimationFrame(step);
      };

      this.frameRequest = requestAnimationFrame(step);
    }

    requestScrollFrame() {
      cancelAnimationFrame(this.frameRequest);
      this.frameRequest = requestAnimationFrame(() => {
        let progress;
        if (this.progressRoot) {
          const rect = this.progressRoot.getBoundingClientRect();
          progress = -rect.top / Math.max(1, rect.height - window.innerHeight);
        } else {
          const rect = this.getBoundingClientRect();
          progress = -rect.top / Math.max(1, rect.height);
        }
        progress = Math.min(1, Math.max(0, progress));
        this.draw(Math.round(progress * (this.images.length - 1)));
        this.dispatchEvent(new CustomEvent('mc-sequence:progress', { bubbles: true, detail: { progress } }));
      });
    }
  }

  customElements.define('mc-hero-sequence', MontecapraHeroSequence);
}
